<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const isCreatingAttribute = ref(false);
const attributeCreated = ref(false);

const responseTypes = [
  {
    value: 'text',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.RESPONSE_TYPES.TEXT'),
  },
  {
    value: 'number',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.RESPONSE_TYPES.NUMBER'),
  },
  {
    value: 'email',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.RESPONSE_TYPES.EMAIL'),
  },
  {
    value: 'phone_number',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.RESPONSE_TYPES.PHONE'),
  },
  {
    value: 'date',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.RESPONSE_TYPES.DATE'),
  },
];

const timeoutUnits = [
  {
    value: 'seconds',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.UNITS.SECONDS'),
  },
  {
    value: 'minutes',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.UNITS.MINUTES'),
  },
  {
    value: 'hours',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.UNITS.HOURS'),
  },
  {
    value: 'days',
    label: t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.UNITS.DAYS'),
  },
];

const localData = ref({
  prompt_message: props.data?.prompt_message || '',
  response_type: props.data?.response_type || 'text',
  attribute_key: props.data?.attribute_key || '',
  attribute_display_name: props.data?.attribute_display_name || '',
  timeout_value: props.data?.timeout_value || 5,
  timeout_unit: props.data?.timeout_unit || 'minutes',
});

const useExistingAttribute = ref(Boolean(props.data?.attribute_key));
const newAttributeName = ref(props.data?.attribute_display_name || '');
const newAttributeKey = ref(props.data?.attribute_key || '');

const availableAttributes = computed(() => {
  const contactAttrs = getters['attributes/getContactAttributes'].value || [];
  const conversationAttrs =
    getters['attributes/getConversationAttributes'].value || [];
  return [...contactAttrs, ...conversationAttrs];
});

const selectedAttribute = computed(() =>
  availableAttributes.value.find(
    attr => attr.attributeKey === localData.value.attribute_key
  )
);

onMounted(() => {
  store.dispatch('attributes/get');
});

const emitUpdate = () => {
  let attributeKey = localData.value.attribute_key;
  let attributeDisplayName = localData.value.attribute_display_name;

  if (useExistingAttribute.value) {
    attributeKey =
      selectedAttribute.value?.attributeKey || localData.value.attribute_key;
    attributeDisplayName =
      selectedAttribute.value?.attributeDisplayName ||
      selectedAttribute.value?.attributeKey ||
      localData.value.attribute_key;
  } else {
    attributeKey = newAttributeKey.value;
    attributeDisplayName = newAttributeName.value;
  }

  emit('update', {
    prompt_message: localData.value.prompt_message,
    response_type: localData.value.response_type,
    attribute_key: attributeKey,
    attribute_display_name: attributeDisplayName,
    timeout_value: localData.value.timeout_value,
    timeout_unit: localData.value.timeout_unit,
  });
};

const updateKeyFromName = () => {
  if (!useExistingAttribute.value) {
    newAttributeKey.value = newAttributeName.value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
    attributeCreated.value = false;
  }
  emitUpdate();
};

const canCreateAttribute = computed(
  () =>
    newAttributeName.value.trim() &&
    newAttributeKey.value.trim() &&
    !attributeCreated.value
);

const createAttribute = async () => {
  if (!canCreateAttribute.value) return;
  isCreatingAttribute.value = true;
  try {
    const responseTypeMap = {
      text: 0,
      number: 1,
      currency: 2,
      percent: 3,
      link: 4,
      date: 5,
      list: 6,
      checkbox: 7,
    };
    await store.dispatch('attributes/create', {
      attribute_display_name: newAttributeName.value.trim(),
      attribute_key: newAttributeKey.value.trim(),
      attribute_model: 'contact_attribute',
      attribute_display_type:
        responseTypeMap[localData.value.response_type] ?? 0,
    });
    attributeCreated.value = true;
    useAlert(t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_CREATED'));
    emitUpdate();
  } catch (error) {
    useAlert(
      error?.message ||
        t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_CREATE_ERROR')
    );
  } finally {
    isCreatingAttribute.value = false;
  }
};

watch(
  [localData, useExistingAttribute, newAttributeName, newAttributeKey],
  emitUpdate,
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-col gap-1.5">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.PROMPT_LABEL') }}
      </label>
      <textarea
        v-model="localData.prompt_message"
        :placeholder="
          t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.PROMPT_PLACEHOLDER')
        "
        rows="4"
        class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5 resize-none"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.RESPONSE_TYPE_LABEL') }}
      </label>
      <select
        v-model="localData.response_type"
        class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
      >
        <option
          v-for="type in responseTypes"
          :key="type.value"
          :value="type.value"
        >
          {{ type.label }}
        </option>
      </select>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <input
          id="existingAttr"
          v-model="useExistingAttribute"
          type="radio"
          :value="true"
          class="accent-n-blue-5"
        />
        <label for="existingAttr" class="text-xs font-medium text-n-slate-11">
          {{
            t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.USE_EXISTING_ATTRIBUTE')
          }}
        </label>
      </div>
      <select
        v-if="useExistingAttribute"
        v-model="localData.attribute_key"
        class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
      >
        <option value="">
          {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.SELECT_ATTRIBUTE') }}
        </option>
        <option
          v-for="attr in availableAttributes"
          :key="attr.attributeKey"
          :value="attr.attributeKey"
        >
          {{ attr.attributeDisplayName }}
          <span
            v-if="attr.attributeModel === 'conversation_attribute'"
            class="text-n-slate-9"
          >
            ({{
              t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.CONVERSATION')
            }})</span
          >
        </option>
      </select>

      <div class="flex items-center gap-2 mt-1">
        <input
          id="newAttr"
          v-model="useExistingAttribute"
          type="radio"
          :value="false"
          class="accent-n-blue-5"
        />
        <label for="newAttr" class="text-xs font-medium text-n-slate-11">
          {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.CREATE_ATTRIBUTE') }}
        </label>
      </div>
      <div v-if="!useExistingAttribute" class="flex flex-col gap-1.5">
        <input
          v-model="newAttributeName"
          type="text"
          :placeholder="
            t(
              'FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_NAME_PLACEHOLDER'
            )
          "
          class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
          @input="updateKeyFromName"
        />
        <input
          v-model="newAttributeKey"
          type="text"
          :placeholder="
            t(
              'FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_KEY_PLACEHOLDER'
            )
          "
          class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
        />
        <div
          v-if="attributeCreated"
          class="flex items-center gap-1.5 text-xs text-n-teal-11 mt-1"
        >
          <span class="i-lucide-check-circle w-4 h-4" />
          {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_CREATED') }}
        </div>
        <button
          v-else
          type="button"
          :disabled="!canCreateAttribute || isCreatingAttribute"
          class="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-n-blue-5 text-white hover:bg-n-blue-6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-1"
          @click="createAttribute"
        >
          <span
            v-if="isCreatingAttribute"
            class="i-lucide-loader-2 w-4 h-4 animate-spin"
          />
          <span v-else class="i-lucide-plus w-4 h-4" />
          {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.CREATE_BUTTON') }}
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.TIMEOUT_LABEL') }}
      </label>
      <div class="flex items-center gap-2">
        <input
          v-model.number="localData.timeout_value"
          type="number"
          min="1"
          class="flex-1 px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
        />
        <select
          v-model="localData.timeout_unit"
          class="flex-1 px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
        >
          <option
            v-for="unit in timeoutUnits"
            :key="unit.value"
            :value="unit.value"
          >
            {{ unit.label }}
          </option>
        </select>
      </div>
    </div>

    <div
      class="flex items-start gap-2 p-3 rounded-lg bg-n-amber-2/50 text-n-amber-11 text-xs"
    >
      <span class="i-lucide-info w-4 h-4 shrink-0" />
      <span>
        {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.PAUSE_NOTICE') }}
      </span>
    </div>
  </div>
</template>
